import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth-service.service';
import { RegistrationService } from 'src/app/services/registration.service';
import { catchError, take } from 'rxjs/operators';
import { Subject, takeUntil, throwError } from 'rxjs';

const PASSWORD_SYMBOLS_PATTERN: RegExp = /^\S*$/;

@Component({
  selector: 'app-forgot-password',
  templateUrl: './forgot-password.component.html',
  styleUrls: ['./forgot-password.component.scss']
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  presetEmail?: string;

  currentStep = 1;
  emailAddress: string = '';
  digits = [0, 1, 2, 3, 4, 5];
  otp: string[] = ['', '', '', '', '', ''];
  submitCodeErrorFlag: boolean = false;
  changePasswordError: boolean;
  requestAlreadySent = false;
  emailForm: FormGroup = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email])
  });

  passwordForm: FormGroup = new FormGroup({
    password: new FormControl('', [
      Validators.required,
      Validators.pattern(PASSWORD_SYMBOLS_PATTERN)
    ]),

    passwordRepeat: new FormControl('', [
      Validators.required,
      Validators.pattern(PASSWORD_SYMBOLS_PATTERN)
    ])
  });

  private readonly destroy$ = new Subject<void>();

  constructor(
    private router: Router,
    private authService: AuthService,
    private route: ActivatedRoute,
    private registrationService: RegistrationService
  ) {
    const passwordControl = this.passwordForm.get('password');
    const passwordRepeatControl = this.passwordForm.get('passwordRepeat');

    passwordControl!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkPasswordMatch();
      });

    passwordRepeatControl!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.checkPasswordMatch();
      });
  }

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.presetEmail = params['email'];
        if (this.presetEmail) {
          this.emailForm.get('email')?.setValue(this.presetEmail);
          this.requestResetPassword();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkPasswordMatch() {
    const password = this.passwordForm.get('password')?.value;
    const passwordRepeat = this.passwordForm.get('passwordRepeat')?.value;

    const regex = /^\S*$/; // Регулярное выражение
    let regexFlag = true;

    if (
      !password ||
      !passwordRepeat ||
      !regex.test(password) ||
      !regex.test(passwordRepeat)
    ) {
      regexFlag = false;
    }

    if (password === passwordRepeat && regexFlag) {
      this.passwordForm.get('passwordRepeat')!.setErrors(null);
    }
    if (password !== passwordRepeat && regexFlag) {
      this.passwordForm.get('passwordRepeat')!.setErrors({ mismatch: true });
    }
  }

  openLogin() {
    return this.router.navigate(['auth']).then(() => {
      this.resetForms();
    });
  }

  backToEmail() {
    this.currentStep = 1;
  }

  requestResetPassword() {
    this.registrationService
      .requestResetPassword(this.emailForm.get('email')?.value)
      .pipe(
        take(1),
        catchError((error: any) => {
          if (error.status === 409) {
            this.requestAlreadySent = true;
            this.currentStep = 2;
          }
          return throwError(error);
        })
      )
      .subscribe({
        next: () => {
          this.requestAlreadySent = false;
          this.currentStep = 2;
        },
        error: () => {}
      });
  }

  focusNextInput(index: number) {
    // Фокусируем на следующем поле при вводе
    if (index < this.digits.length - 1 && this.otp[index]) {
      document.getElementById(`otp${index + 1}`)!.focus();
    }
    if (index > 0 && !this.otp[index]) {
      if (this.submitCodeErrorFlag) this.submitCodeErrorFlag = false;

      document.getElementById(`otp${index - 1}`)!.focus();
    }

    if (index === this.digits.length - 1 && this.otp[index]) {
      this.registrationService
        .changePasswordCheckCode(
          this.emailForm.get('email')!.value!,
          this.otp.join('')
        )
        .pipe(take(1))
        .subscribe(
          res => {
            if (res.data) {
              this.currentStep = 3;
            } else {
              this.setWrongCode();
            }
          },
          () => {
            this.setWrongCode();
          }
        );
    }
  }

  changePassword() {
    this.registrationService
      .changePassword(
        this.emailForm.get('email')!.value!,
        this.passwordForm.get('password')?.value,
        this.otp.join('')
      )
      .pipe(take(1))
      .subscribe(
        () => {
          this.changePasswordError = false;
          // Используем Promise от openLogin для навигации
          this.openLogin();
        },
        () => {
          this.setWrongCode();
        }
      );
  }

  setWrongCode() {
    document.getElementById('otp0')!.focus();
    this.changePasswordError = true;
    this.currentStep = 2;
    this.otp = ['', '', '', '', '', ''];
  }

  private resetForms() {
    this.emailForm.reset();
    this.passwordForm.reset();
    this.otp = ['', '', '', '', '', ''];
    this.currentStep = 1;
  }
}
